import { Injectable } from '@angular/core';

import { from, Observable, of } from 'rxjs';
import { every, first, map, mergeMap } from 'rxjs/operators';

import { IOpenModalLogicalUnitParams } from '../logicalunit/logicalunit-modal.interface';
import { RxjsService } from '../utils/rxjs.service';

/**
 * Determines whether an open logical-unit modal may be dismissed/closed given its
 * `canDeactivate` guards (e.g. unsaved changes). Used as the closeInterceptor of the underlying
 * ngx-bootstrap modal — see {@link U2000_ModalProvider.openComponent}.
 */
@Injectable()
export class ModalDismissGuard {
    openingParams: IOpenModalLogicalUnitParams<any>;

    constructor(private rxjsService: RxjsService) {}

    beforeDismiss(): boolean | Promise<any> {
        return this.runCanDeactivate(this.openingParams).toPromise();
    }

    beforeClose(): boolean | Promise<any> {
        return this.runCanDeactivate(this.openingParams).toPromise();
    }

    setOpeningParams(params: IOpenModalLogicalUnitParams<any>) {
        this.openingParams = params;
    }

    /**
     * Adapts {@link beforeDismiss} (resolves to a "should block" boolean, the ngx-modialog-7
     * `CloseGuard` convention) to ngx-bootstrap's `CloseInterceptorFn` contract: resolve to allow
     * the modal to close, reject to keep it open. Assign the returned function to
     * `ModalOptions.closeInterceptor` when opening a logical-unit modal.
     */
    asCloseInterceptor(): () => Promise<void> {
        return () =>
            Promise.resolve(this.beforeDismiss()).then(shouldBlock => {
                if (shouldBlock) {
                    return Promise.reject();
                }
                return Promise.resolve();
            });
    }

    private runCanDeactivate(params: IOpenModalLogicalUnitParams<any>) {
        const canDeactivate = params.canDeactivate;
        if (!canDeactivate || canDeactivate.length === 0) {
            return of(true);
        }
        const canDeactivate$ = from(canDeactivate).pipe(
            mergeMap(c => {
                // Explicit `any`: under Angular 20's recommended `moduleResolution: "bundler"`
                // tsconfig (see docs/components/SAIA-23489-impl-plan.md, Hop 6), `Injector.get(c)`
                // here infers as `unknown` instead of `any` even though `c`/`canDeactivate` are
                // both plainly typed `any` — a type-checking-only quirk (not a runtime change),
                // not worth chasing through Angular's internal .d.ts structure for a
                // dependency-migration ticket. Asserted back to `any`, matching the pre-Hop-6
                // inferred type and preserving the exact existing runtime behavior below.
                const guard: any = params.injector.get(c);
                let observable: Observable<any>;

                if (guard.canDeactivate) {
                    observable = this.rxjsService.wrapIntoObservable(guard.canDeactivate());
                } else {
                    observable = this.rxjsService.wrapIntoObservable(guard());
                }
                return observable.pipe(first());
            }),
        );
        return canDeactivate$.pipe(
            every(result => result === true),
            map(x => !x),
        );
    }
}
